import { create } from 'zustand'
import { persist, createJSONStorage, devtools } from 'zustand/middleware'

// View types
export type ViewType = 'list' | 'kanban' | 'gantt' | 'calendar' | 'timeline' | 'board'

// Notification types
export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface ToastNotification {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number
  dismissible?: boolean
  action?: {
    label: string
    onClick: () => void
  }
  createdAt: number
}

// Modal keys for type safety
export type ModalKey =
  | 'createProject'
  | 'editProject'
  | 'deleteProject'
  | 'createTask'
  | 'editTask'
  | 'deleteTask'
  | 'taskDetail'
  | 'settings'
  | 'userProfile'
  | 'invite'
  | 'filter'
  | 'export'
  | 'import'
  | 'confirm'
  | string // Allow custom modal keys

// Theme type
export type Theme = 'light' | 'dark' | 'system'

interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean
  sidebarPinned: boolean

  // View state
  currentView: ViewType
  previousView: ViewType | null

  // Modal state
  modals: Record<ModalKey, boolean>
  modalData: Record<ModalKey, unknown>

  // Notifications/Toasts
  notifications: ToastNotification[]

  // Theme preferences
  theme: Theme
  accentColor: string

  // Layout preferences
  compactMode: boolean
  showBreadcrumbs: boolean
  showQuickActions: boolean

  // Command palette
  commandPaletteOpen: boolean

  // Loading states for UI operations
  isPageLoading: boolean

  // Actions - Sidebar
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebarPin: () => void

  // Actions - View
  setView: (view: ViewType) => void
  goToPreviousView: () => void

  // Actions - Modals
  openModal: (key: ModalKey, data?: unknown) => void
  closeModal: (key: ModalKey) => void
  toggleModal: (key: ModalKey) => void
  closeAllModals: () => void
  getModalData: <T>(key: ModalKey) => T | undefined

  // Actions - Notifications
  addNotification: (notification: Omit<ToastNotification, 'id' | 'createdAt'>) => string
  removeNotification: (id: string) => void
  clearNotifications: () => void

  // Convenience notification methods
  showSuccess: (title: string, message?: string) => string
  showError: (title: string, message?: string) => string
  showWarning: (title: string, message?: string) => string
  showInfo: (title: string, message?: string) => string

  // Actions - Theme
  setTheme: (theme: Theme) => void
  setAccentColor: (color: string) => void

  // Actions - Layout
  setCompactMode: (compact: boolean) => void
  toggleCompactMode: () => void
  setShowBreadcrumbs: (show: boolean) => void
  setShowQuickActions: (show: boolean) => void

  // Actions - Command Palette
  openCommandPalette: () => void
  closeCommandPalette: () => void
  toggleCommandPalette: () => void

  // Actions - Loading
  setPageLoading: (loading: boolean) => void

  // Reset
  resetUIState: () => void
}

// Generate unique ID for notifications
const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Default notification duration in milliseconds
const DEFAULT_NOTIFICATION_DURATION = 4000

const initialState = {
  sidebarCollapsed: false,
  sidebarPinned: true,
  currentView: 'kanban' as ViewType,
  previousView: null as ViewType | null,
  modals: {} as Record<ModalKey, boolean>,
  modalData: {} as Record<ModalKey, unknown>,
  notifications: [] as ToastNotification[],
  theme: 'system' as Theme,
  accentColor: '#6366f1', // Indigo
  compactMode: false,
  showBreadcrumbs: true,
  showQuickActions: true,
  commandPaletteOpen: false,
  isPageLoading: false,
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        ...initialState,

        // Sidebar actions
        toggleSidebar: () =>
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

        toggleSidebarPin: () =>
          set((state) => ({ sidebarPinned: !state.sidebarPinned })),

        // View actions
        setView: (view) =>
          set((state) => ({
            previousView: state.currentView,
            currentView: view,
          })),

        goToPreviousView: () =>
          set((state) => ({
            currentView: state.previousView || state.currentView,
            previousView: null,
          })),

        // Modal actions
        openModal: (key, data) =>
          set((state) => ({
            modals: { ...state.modals, [key]: true },
            modalData: data !== undefined ? { ...state.modalData, [key]: data } : state.modalData,
          })),

        closeModal: (key) =>
          set((state) => {
            const { [key]: _, ...restModals } = state.modals
            const { [key]: __, ...restData } = state.modalData
            return {
              modals: { ...restModals, [key]: false },
              modalData: restData,
            }
          }),

        toggleModal: (key) =>
          set((state) => ({
            modals: { ...state.modals, [key]: !state.modals[key] },
          })),

        closeAllModals: () =>
          set({
            modals: {},
            modalData: {},
          }),

        getModalData: <T>(key: ModalKey) => get().modalData[key] as T | undefined,

        // Notification actions
        addNotification: (notification) => {
          const id = generateId()
          const newNotification: ToastNotification = {
            ...notification,
            id,
            duration: notification.duration ?? DEFAULT_NOTIFICATION_DURATION,
            dismissible: notification.dismissible ?? true,
            createdAt: Date.now(),
          }

          set((state) => ({
            notifications: [...state.notifications, newNotification],
          }))

          // Auto-remove notification after duration
          if (newNotification.duration && newNotification.duration > 0) {
            setTimeout(() => {
              get().removeNotification(id)
            }, newNotification.duration)
          }

          return id
        },

        removeNotification: (id) =>
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          })),

        clearNotifications: () => set({ notifications: [] }),

        // Convenience notification methods
        showSuccess: (title, message) =>
          get().addNotification({ type: 'success', title, message, duration: 4000 }),

        showError: (title, message) =>
          get().addNotification({ type: 'error', title, message, duration: 6000 }),

        showWarning: (title, message) =>
          get().addNotification({ type: 'warning', title, message }),

        showInfo: (title, message) =>
          get().addNotification({ type: 'info', title, message, duration: 4000 }),

        // Theme actions
        setTheme: (theme) => set({ theme }),

        setAccentColor: (color) => set({ accentColor: color }),

        // Layout actions
        setCompactMode: (compact) => set({ compactMode: compact }),

        toggleCompactMode: () =>
          set((state) => ({ compactMode: !state.compactMode })),

        setShowBreadcrumbs: (show) => set({ showBreadcrumbs: show }),

        setShowQuickActions: (show) => set({ showQuickActions: show }),

        // Command palette actions
        openCommandPalette: () => set({ commandPaletteOpen: true }),

        closeCommandPalette: () => set({ commandPaletteOpen: false }),

        toggleCommandPalette: () =>
          set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

        // Loading actions
        setPageLoading: (loading) => set({ isPageLoading: loading }),

        // Reset
        resetUIState: () =>
          set({
            ...initialState,
            // Keep persisted preferences
            theme: get().theme,
            accentColor: get().accentColor,
            compactMode: get().compactMode,
          }),
      }),
      {
        name: 'projectpulse-ui',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          // Only persist user preferences
          sidebarCollapsed: state.sidebarCollapsed,
          sidebarPinned: state.sidebarPinned,
          currentView: state.currentView,
          theme: state.theme,
          accentColor: state.accentColor,
          compactMode: state.compactMode,
          showBreadcrumbs: state.showBreadcrumbs,
          showQuickActions: state.showQuickActions,
        }),
      }
    ),
    { name: 'ui-store' }
  )
)

// Selector hooks for better performance
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed)
export const useSidebarPinned = () => useUIStore((state) => state.sidebarPinned)
export const useCurrentView = () => useUIStore((state) => state.currentView)
export const useTheme = () => useUIStore((state) => state.theme)
export const useAccentColor = () => useUIStore((state) => state.accentColor)
export const useCompactMode = () => useUIStore((state) => state.compactMode)
export const useCommandPaletteOpen = () => useUIStore((state) => state.commandPaletteOpen)
export const usePageLoading = () => useUIStore((state) => state.isPageLoading)
export const useNotifications = () => useUIStore((state) => state.notifications)

// Modal state selector
export const useModalOpen = (key: ModalKey) => useUIStore((state) => !!state.modals[key])
export const useModalData = <T>(key: ModalKey) =>
  useUIStore((state) => state.modalData[key] as T | undefined)

// Check if any modal is open
export const useAnyModalOpen = () =>
  useUIStore((state) => Object.values(state.modals).some(Boolean))

// Export type for external use
export type { UIState }
